import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Search, Calendar, ArrowRight, Eye, BookOpen, Filter } from 'lucide-react';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_COLORS = {
  'strabismo': { bg: 'bg-sky-50', text: 'text-sky-700', accent: '#0ea5e9', gradient: 'from-sky-400 to-sky-600' },
  'cataratta': { bg: 'bg-indigo-50', text: 'text-indigo-700', accent: '#6366f1', gradient: 'from-indigo-400 to-indigo-600' },
  'glaucoma': { bg: 'bg-emerald-50', text: 'text-emerald-700', accent: '#10b981', gradient: 'from-emerald-400 to-emerald-600' },
  'maculopatia': { bg: 'bg-rose-50', text: 'text-rose-700', accent: '#f43f5e', gradient: 'from-rose-400 to-rose-600' },
  'miopia': { bg: 'bg-cyan-50', text: 'text-cyan-700', accent: '#06b6d4', gradient: 'from-cyan-400 to-cyan-600' },
  'chirurgia refrattiva': { bg: 'bg-violet-50', text: 'text-violet-700', accent: '#8b5cf6', gradient: 'from-violet-400 to-violet-600' },
  'ambliopia': { bg: 'bg-teal-50', text: 'text-teal-700', accent: '#14b8a6', gradient: 'from-teal-400 to-teal-600' },
  'prevenzione': { bg: 'bg-amber-50', text: 'text-amber-700', accent: '#f59e0b', gradient: 'from-amber-400 to-amber-600' },
  'vizi refrattivi': { bg: 'bg-blue-50', text: 'text-blue-700', accent: '#3b82f6', gradient: 'from-blue-400 to-blue-600' },
  'calazio': { bg: 'bg-orange-50', text: 'text-orange-700', accent: '#f97316', gradient: 'from-orange-400 to-orange-600' },
  'occhio secco': { bg: 'bg-yellow-50', text: 'text-yellow-700', accent: '#eab308', gradient: 'from-yellow-400 to-yellow-600' },
  'cheratocono': { bg: 'bg-purple-50', text: 'text-purple-700', accent: '#a855f7', gradient: 'from-purple-400 to-purple-600' },
  'videoterminali': { bg: 'bg-slate-50', text: 'text-slate-700', accent: '#64748b', gradient: 'from-slate-400 to-slate-600' },
  'meibomio': { bg: 'bg-lime-50', text: 'text-lime-700', accent: '#84cc16', gradient: 'from-lime-400 to-lime-600' },
  'default': { bg: 'bg-gray-50', text: 'text-gray-700', accent: '#6b7280', gradient: 'from-gray-400 to-gray-600' },
};

const categories = [
  { id: 'all', name: 'Tutti' },
  { id: 'strabismo', name: 'Strabismo' },
  { id: 'cataratta', name: 'Cataratta' },
  { id: 'glaucoma', name: 'Glaucoma' },
  { id: 'maculopatia', name: 'Maculopatia' },
  { id: 'miopia', name: 'Miopia' },
  { id: 'chirurgia refrattiva', name: 'Chirurgia Refrattiva' },
  { id: 'ambliopia', name: 'Ambliopia' },
  { id: 'cheratocono', name: 'Cheratocono' },
  { id: 'occhio secco', name: 'Occhio Secco' },
  { id: 'meibomio', name: 'Gh. Meibomio' },
  { id: 'videoterminali', name: 'Videoterminali' },
  { id: 'prevenzione', name: 'Prevenzione' },
  { id: 'vizi refrattivi', name: 'Vizi Refrattivi' },
  { id: 'calazio', name: 'Calazio' },
];

const Blog = () => {
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => { fetchArticles(); }, []);
  useEffect(() => { filterArticles(); }, [searchTerm, selectedCategory, articles]);

  const fetchArticles = async () => {
    try {
      const [seoRes, blogRes] = await Promise.all([
        fetch(`${API_URL}/api/seo-articles`),
        fetch(`${API_URL}/api/blog-articles`)
      ]);
      let seoArticles = seoRes.ok ? (await seoRes.json()).articles || [] : [];
      let blogArticles = blogRes.ok ? (await blogRes.json()).articles || [] : [];
      const allArticles = [...seoArticles, ...blogArticles].sort((a, b) =>
        new Date(b.created_at || b.published_at) - new Date(a.created_at || a.published_at)
      );
      setArticles(allArticles);
      setFilteredArticles(allArticles);
    } catch (error) {
      console.error('Errore caricamento articoli:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterArticles = () => {
    let filtered = [...articles];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(a =>
        a.title?.toLowerCase().includes(term) ||
        a.meta_description?.toLowerCase().includes(term) ||
        a.keywords?.toLowerCase().includes(term)
      );
    }
    if (selectedCategory !== 'all') {
      const cat = selectedCategory.toLowerCase();
      filtered = filtered.filter(a => {
        const articleCat = (a.category || '').toLowerCase();
        return articleCat.includes(cat) || cat.includes(articleCat) ||
          a.title?.toLowerCase().includes(cat) ||
          a.keywords?.toLowerCase().includes(cat);
      });
    }
    setFilteredArticles(filtered);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return ''; }
  };

  const getCategoryColor = (article) => {
    const cat = (article.category || '').toLowerCase();
    for (const [key, val] of Object.entries(CATEGORY_COLORS)) {
      if (key !== 'default' && (cat.includes(key) || article.title?.toLowerCase().includes(key))) return val;
    }
    return CATEGORY_COLORS.default;
  };

  const getMainTopic = (article) => {
    const topics = {
      'miopia': 'Miopia', 'strabismo': 'Strabismo', 'calazio': 'Calazio',
      'cataratta': 'Cataratta', 'glaucoma': 'Glaucoma', 'maculopatia': 'Maculopatia',
      'cheratocono': 'Cheratocono', 'ambliopia': 'Ambliopia', 'presbiopia': 'Presbiopia',
      'astigmatismo': 'Astigmatismo', 'occhio secco': 'Occhio Secco', 'secchezza': 'Occhio Secco',
      'blefarite': 'Blefarite', 'meibomio': 'Gh. Meibomio', 'videoterminali': 'Videoterminali',
      'computer': 'Videoterminali', '20-20-20': 'Videoterminali',
    };
    const lower = (article.title || '').toLowerCase();
    for (const [key, value] of Object.entries(topics)) {
      if (lower.includes(key)) return value;
    }
    return article.category || 'Oftalmologia';
  };

  return (
    <>
      <Helmet>
        <title>Blog Oftalmologico | PRIME VISION - Articoli su Salute degli Occhi</title>
        <meta name="description" content="Blog di oftalmologia: articoli su strabismo, cataratta, glaucoma, miopia e salute degli occhi. Consigli e novit&agrave; dal centro oculistico PRIME VISION." />
      </Helmet>

      <Navbar />
      <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #f0f7fc 0%, #ffffff 50%, #f8fafe 100%)' }}>
        {/* Hero */}
        <section className="relative overflow-hidden py-16 md:py-20" data-testid="blog-hero">
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #e8f4fd 0%, #dbeafe 50%, #ede9fe 100%)' }} />
          <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #93c5fd33 0%, transparent 50%), radial-gradient(circle at 80% 50%, #a78bfa22 0%, transparent 50%)' }} />
          <div className="relative z-10 container mx-auto px-4 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/70 backdrop-blur-sm border border-sky-200/50 mb-6">
              <BookOpen className="w-4 h-4 text-sky-500" />
              <span className="text-sm font-medium text-sky-600">Il nostro Blog</span>
            </div>
            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-light mb-4 tracking-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1e3a5f' }}
              data-testid="blog-title"
            >
              Salute degli Occhi
            </h1>
            <p className="text-base md:text-lg max-w-2xl mx-auto text-slate-500 leading-relaxed">
              Guide, approfondimenti e novita dai nostri specialisti per prenderti cura della tua vista
            </p>
          </div>
        </section>

        {/* Search & Filters */}
        <section className="sticky top-0 z-30 bg-white/80 backdrop-blur-lg border-b border-slate-100 py-4" data-testid="blog-filters">
          <div className="container mx-auto px-4">
            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Cerca articoli..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-sky-300 focus:border-transparent focus:bg-white transition-all"
                  data-testid="blog-search-input"
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border transition-all ${showFilters ? 'bg-sky-50 border-sky-300 text-sky-700' : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'}`}
                data-testid="blog-filter-toggle"
              >
                <Filter className="w-4 h-4" />
                <span className="hidden sm:inline">Filtra</span>
              </button>
              {selectedCategory !== 'all' && (
                <button
                  onClick={() => setSelectedCategory('all')}
                  className="px-3 py-2.5 rounded-xl text-sm font-medium bg-sky-500 text-white hover:bg-sky-600 transition-all"
                  data-testid="blog-clear-filter"
                >
                  Mostra tutti
                </button>
              )}
            </div>
            {showFilters && (
              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100">
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-all ${
                      selectedCategory === cat.id
                        ? 'bg-sky-500 text-white shadow-sm'
                        : 'bg-slate-100 text-slate-600 hover:bg-sky-50 hover:text-sky-700'
                    }`}
                    data-testid={`blog-cat-${cat.id}`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Articles count */}
        <div className="container mx-auto px-4 pt-8 pb-2">
          <p className="text-sm text-slate-400">
            {filteredArticles.length} articol{filteredArticles.length === 1 ? 'o' : 'i'} {selectedCategory !== 'all' ? `in "${categories.find(c => c.id === selectedCategory)?.name}"` : ''}
          </p>
        </div>

        {/* Articles Grid */}
        <section className="pb-16" data-testid="blog-articles-section">
          <div className="container mx-auto px-4">
            {loading ? (
              <div className="text-center py-20">
                <div className="animate-spin rounded-full h-10 w-10 border-2 border-sky-500 border-t-transparent mx-auto" />
                <p className="mt-4 text-slate-400 text-sm">Caricamento articoli...</p>
              </div>
            ) : filteredArticles.length === 0 ? (
              <div className="text-center py-20">
                <Eye className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                <p className="text-slate-400">Nessun articolo trovato</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article, index) => {
                  const colors = getCategoryColor(article);
                  const topic = getMainTopic(article);
                  return (
                    <Link
                      to={`/articolo/${article.slug || article.id}`}
                      key={article.id || index}
                      className="group block bg-white rounded-2xl border border-slate-100 hover:border-sky-200 hover:shadow-lg hover:shadow-sky-100/50 transition-all duration-300 overflow-hidden"
                      data-testid={`blog-article-${index}`}
                    >
                      {/* Topic Header */}
                      <div className={`h-36 flex flex-col items-center justify-center bg-gradient-to-br ${colors.gradient} relative overflow-hidden`}>
                        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 30% 70%, white 0%, transparent 60%)' }} />
                        <span className="relative text-white/60 text-xs font-medium tracking-widest uppercase mb-1">{article.category || 'Oftalmologia'}</span>
                        <h3 className="relative text-2xl font-light text-white tracking-wide" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                          {topic}
                        </h3>
                      </div>
                      {/* Content */}
                      <div className="p-5">
                        <div className="flex items-center gap-2 text-xs text-slate-400 mb-2.5">
                          <Calendar className="w-3.5 h-3.5" />
                          <span>{formatDate(article.created_at || article.published_at)}</span>
                        </div>
                        <h2 className="text-base font-semibold text-slate-800 mb-2 line-clamp-2 group-hover:text-sky-600 transition-colors leading-snug">
                          {article.title}
                        </h2>
                        <p className="text-sm text-slate-500 mb-4 line-clamp-2 leading-relaxed">
                          {article.excerpt || article.meta_description || article.summary || ''}
                        </p>
                        <span className="inline-flex items-center text-xs font-medium text-sky-500 group-hover:text-sky-600 transition-colors">
                          Leggi articolo <ArrowRight className="w-3.5 h-3.5 ml-1 group-hover:translate-x-0.5 transition-transform" />
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="py-16" style={{ background: 'linear-gradient(135deg, #e8f4fd 0%, #dbeafe 50%, #ede9fe 100%)' }}>
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-2xl md:text-3xl font-light mb-3 text-slate-700" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Hai domande sulla salute dei tuoi occhi?
            </h2>
            <p className="text-slate-500 mb-8 max-w-xl mx-auto text-sm">
              Il nostro team di specialisti e a tua disposizione
            </p>
            <Link
              to="/booking"
              className="inline-flex items-center px-8 py-3.5 rounded-xl font-medium text-sm bg-sky-500 text-white hover:bg-sky-600 transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5"
              data-testid="blog-cta-book"
            >
              Prenota una Visita
            </Link>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default Blog;
